import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/I18nContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Lock, ShieldAlert } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [locked, setLocked] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      toast.success(t("toast.welcome"));
      nav(loc.state?.from || "/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || t("toast.loginFailed");
      const status = err?.response?.status;
      setError(msg);
      if (status === 429) setLocked(true);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] paper-bg grid place-items-center px-4 py-12">
      <div className="w-full max-w-md card-classic p-8" data-testid="login-page">
        <h1 className="font-serif-display text-3xl font-bold text-[#0F172A]">{t("login.title")}</h1>
        <p className="text-[#475569] mt-1">{t("login.subtitle")}</p>

        {error && (
          <div
            className={`mt-4 p-3 rounded-md border text-sm flex items-start gap-2 ${
              locked
                ? "border-[#DC2626] bg-red-50 text-[#7F1D1D]"
                : "border-amber-200 bg-amber-50 text-amber-900"
            }`}
            data-testid="login-error-banner"
          >
            {locked ? <Lock size={16} className="mt-0.5"/> : <ShieldAlert size={16} className="mt-0.5"/>}
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="email">{t("auth.email")}</Label>
            <Input id="email" type="email" required value={email}
              onChange={(e)=>setEmail(e.target.value)}
              data-testid="login-email-input" className="mt-1"/>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Link to="/forgot" className="text-xs font-semibold text-[#0F172A] underline" data-testid="login-forgot-link">
                {t("login.forgot")}
              </Link>
            </div>
            <Input id="password" type="password" required value={password}
              onChange={(e)=>setPassword(e.target.value)}
              data-testid="login-password-input" className="mt-1"/>
          </div>
          <Button type="submit" disabled={busy}
            className="w-full bg-[#0F172A] hover:bg-[#1E293B] h-11"
            data-testid="login-submit-button">
            {busy ? t("login.submitting") : t("login.submit")}
          </Button>
        </form>
        <p className="mt-6 text-sm text-[#475569]">
          {t("login.noAccount")} <Link to="/register" className="font-semibold text-[#0F172A] underline">{t("login.createOne")}</Link>
        </p>
      </div>
    </div>
  );
}
